import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FileSpreadsheet, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api, downloadPdf, formatApiError, inr } from "@/lib/api";
import { Empty, Panel, marginCls } from "./common";

const short = (v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${Math.round(v / 1000)}k` : `₹${v}`);

const TopPackages = ({ list }) => (
  <Panel className="p-6 min-w-0" data-testid="report-top-packages">
    <p className="font-semibold text-navy flex items-center gap-2"><Trophy className="h-4 w-4 text-coral" /> Best-selling packages</p>
    {list.length === 0 ? <Empty testId="report-top-empty">No bookings in this period.</Empty> : (
      <ol className="mt-4 space-y-3">
        {list.map((t, i) => (
          <li key={t.package_name} className="flex items-center gap-3" data-testid={`report-top-package-${i}`}>
            <span className={`h-8 w-8 shrink-0 rounded-full grid place-items-center font-mono text-xs font-semibold ${i === 0 ? "bg-ocean text-white" : "bg-sky-50 text-ocean"}`}>{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-navy truncate">{t.package_name}</p>
              <p className="text-xs text-slate-500">{t.bookings} booking{t.bookings === 1 ? "" : "s"} · {t.guests} guests</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-sm">{inr(t.revenue)}</p>
              <p className={`font-mono text-xs ${marginCls(t.margin)}`}>{t.margin}%</p>
            </div>
          </li>
        ))}
      </ol>
    )}
  </Panel>
);

const MonthTable = ({ rows, totals }) => (
  <div className="mt-6 overflow-x-auto">
    <table className="w-full min-w-[640px] text-sm" data-testid="report-month-table">
      <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
        <th className="py-2 font-semibold">Month</th><th className="py-2 text-right font-semibold">Bookings</th><th className="py-2 text-right font-semibold">Guests</th>
        <th className="py-2 text-right font-semibold">Revenue</th><th className="py-2 text-right font-semibold">Cost</th><th className="py-2 text-right font-semibold">Profit</th><th className="py-2 text-right font-semibold">Margin</th>
      </tr></thead>
      <tbody>
        {[...rows].reverse().map((r) => (
          <tr key={r.month} className="border-b border-slate-100" data-testid={`report-month-${r.month}`}>
            <td className="py-2 text-navy">{r.label}</td><td className="py-2 text-right font-mono">{r.bookings}</td><td className="py-2 text-right font-mono">{r.guests}</td>
            <td className="py-2 text-right font-mono">{inr(r.revenue)}</td><td className="py-2 text-right font-mono text-slate-500">{inr(r.cost)}</td>
            <td className={`py-2 text-right font-mono ${marginCls(r.margin)}`}>{inr(r.profit)}</td><td className={`py-2 text-right font-mono ${marginCls(r.margin)}`}>{r.margin}%</td>
          </tr>
        ))}
        <tr className="font-semibold bg-mist" data-testid="report-totals-row">
          <td className="py-2 px-1 text-navy">Total</td><td className="py-2 text-right font-mono">{totals.bookings}</td><td className="py-2 text-right font-mono">{totals.guests}</td>
          <td className="py-2 text-right font-mono">{inr(totals.revenue)}</td><td className="py-2 text-right font-mono">{inr(totals.cost)}</td>
          <td className="py-2 text-right font-mono text-emerald-600">{inr(totals.profit)}</td><td className="py-2 text-right font-mono">{totals.margin}%</td>
        </tr>
      </tbody>
    </table>
  </div>
);

export const MonthlyReport = () => {
  const [months, setMonths] = useState("12");
  const [basis, setBasis] = useState("booked");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const m = data.months;
      await downloadPdf(`/admin/reports/monthly.xlsx?months=${months}&basis=${basis}`, `ocean-wings-report-${m[0].month}-to-${m[m.length - 1].month}.xlsx`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setExporting(false); }
  };
  useEffect(() => { api.get("/admin/reports/monthly", { params: { months, basis } }).then((r) => setData(r.data)); }, [months, basis]);

  return (
    <div className="mt-6 grid gap-6 grid-cols-1 xl:grid-cols-3" data-testid="monthly-report">
      <Panel className="p-6 xl:col-span-2 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-navy">Monthly revenue &amp; profit</p>
            <p className="text-xs text-slate-500">Cancelled bookings excluded. Grouped by {basis === "booked" ? "booking date" : "travel date"}.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="h-9 border-emerald-200 text-emerald-700 hover:bg-emerald-50" disabled={!data || exporting} onClick={exportXlsx} data-testid="report-export-excel-button">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Excel
            </Button>
            <Select value={basis} onValueChange={setBasis}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="report-basis-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="booked">By booking date</SelectItem><SelectItem value="travel">By travel date</SelectItem></SelectContent>
            </Select>
            <Select value={months} onValueChange={setMonths}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="report-months-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{["6", "12", "24"].map((m) => <SelectItem key={m} value={m}>Last {m} months</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {!data ? <Skeleton className="h-72 mt-6 rounded-xl" /> : (
          <>
            <div className="mt-6 h-72" data-testid="report-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={data.months} margin={{ left: -10, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={(v) => v.split(" ")[0]} />
                  <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={short} />
                  <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#0077B6" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="profit" name="Profit" fill="#10B981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <MonthTable rows={data.months} totals={data.totals} />
          </>
        )}
      </Panel>
      {data ? <TopPackages list={data.top_packages} /> : <Skeleton className="h-72 rounded-2xl" />}
    </div>
  );
};
